import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CirclePlay, CirclePause, CircleStop, ExternalLink, FilePen, FilePlus2, FileSignature, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Rotulo, Selecao } from '../../componentes/formulario';
import { AreaSensivel } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import type { Pagina } from '@malgoos/shared';
import { api, del, get, mensagemDeErro, post, traduzirErro } from '../../nucleo/api';
import { carregarMais } from '../../nucleo/foco';
import { data, dataHora, dinheiro, mesAno, nomeParceiro, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarAssinaturas } from './dados';
import { GerarDocumento } from './GerarDocumento';
import { AnexarAssinado } from './AnexarAssinado';
import { CAMINHO_CONTRATOS, COR_COBRANCA, COR_ESTADO, totalDaLinha, type Cobranca, type Contrato } from './tipos';
import { useCamposDaTela } from '../../nucleo/campos-tela';

/**
 * Recorrência no idioma: "Cobrança única" ou "A cada N meses" (com os nomes de mensal, trimestral, semestral e anual); C4:
 * "Semanal", "A cada N semanas", "Diária" e "A cada N dias".
 */
export function textoRecorrencia(t: (k: string, o?: Record<string, unknown>) => string, c: { tipo: string; intervalo_meses: number; unidade_recorrencia?: string }) {
  if (c.tipo === 'avulso') return t('recorrencia.avulso');
  if (c.unidade_recorrencia === 'semana') return c.intervalo_meses === 1 ? t('intervalos.semanal') : t('recorrencia.aCadaSemanas', { count: c.intervalo_meses });
  if (c.unidade_recorrencia === 'dia') return c.intervalo_meses === 1 ? t('recorrencia.diaria') : t('recorrencia.aCadaDias', { count: c.intervalo_meses });
  return [1, 3, 6, 12].includes(c.intervalo_meses) ? t(`intervalos.${c.intervalo_meses}`) : t('recorrencia.aCada', { count: c.intervalo_meses });
}

/**
 * Ficha do contrato (W12.8, W12.10, W12.11): resumo, saúde do faturamento, ações (pausar, retomar, ativar, tentar de novo,
 * encerrar com confirmação, excluir rascunho), cobranças agendadas e emitidas, reajustes e chatter.
 */
export function DetalheContrato({ contrato: c, aoEditar, aoExcluir }: { contrato: Contrato; aoEditar: () => void; aoExcluir: () => void }) {
  const { t } = useTranslation('assinaturas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [encerrando, setEncerrando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [reativando, setReativando] = useState(false);
  const [gerandoDocumento, setGerandoDocumento] = useState(false);
  const [gerandoAditivo, setGerandoAditivo] = useState(false);
  const [anexandoAssinado, setAnexandoAssinado] = useState(false);
  const [motivo, setMotivo] = useState('');
  const podeEditar = s.pode('assinaturas.editar') && c.estado !== 'encerrado';
  // Val5 (29/10): pausar, retomar e ativar pedem `assinaturas.pausar`; faturar de novo, `assinaturas.faturar`.
  const podePausar = s.pode('assinaturas.pausar') && c.estado !== 'encerrado';
  const podeFaturar = s.pode('assinaturas.faturar') && c.estado !== 'encerrado';
  const podeEmitir = s.pode('contas.criar');
  // Review r38 robustez (M2): a ficha traz as cobranças mais novas; as anteriores vêm por página ("Carregar anteriores").
  const pendentes = c.cobrancas_pendentes;
  const [anteriores, setAnteriores] = useState<Cobranca[]>([]);
  const [cursorAnteriores, setCursorAnteriores] = useState(c.cobrancas_anteriores_cursor);
  const [carregandoAnteriores, setCarregandoAnteriores] = useState(false);
  useEffect(() => {
    setAnteriores([]);
    setCursorAnteriores(c.cobrancas_anteriores_cursor);
  }, [c.id, c.cobrancas_anteriores_cursor]);
  const buscarAnteriores = async () => {
    if (!cursorAnteriores) return;
    setCarregandoAnteriores(true);
    try {
      const p = await get<Pagina<Cobranca>>(`${CAMINHO_CONTRATOS}/${c.id}/cobrancas`, { cursor: cursorAnteriores, limite: 50 });
      setAnteriores((a) => [...[...p.itens].reverse(), ...a]);
      setCursorAnteriores(p.proximo_cursor);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setCarregandoAnteriores(false);
    }
  };
  const cobrancas = [...anteriores, ...c.cobrancas];
  // A ficha chega depois do drawer abrir: o foco inicial do núcleo iria para o primeiro campo, que aqui é o chatter no
  // fim da ficha (a gaveta rolava até o fim). O foco vai para a primeira ação, no topo, e a ficha abre pelo começo.
  const topo = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const irAoTopo = () => (topo.current?.querySelector<HTMLElement>('button, a') ?? topo.current)?.focus();
    const ativo = document.activeElement;
    if (!ativo || ativo === document.body || ativo.tagName === 'TEXTAREA') irAoTopo();
    // O chatter carrega depois e o foco inicial do núcleo o alcança: até a pessoa interagir (ou 2 s), volta ao topo.
    let interagiu = false;
    const marcar = () => (interagiu = true);
    const aoFocar = (e: FocusEvent) => {
      if (!interagiu && (e.target as HTMLElement | null)?.tagName === 'TEXTAREA') irAoTopo();
    };
    document.addEventListener('pointerdown', marcar, true);
    document.addEventListener('keydown', marcar, true);
    document.addEventListener('focusin', aoFocar);
    const fim = setTimeout(() => document.removeEventListener('focusin', aoFocar), 2000);
    return () => {
      clearTimeout(fim);
      document.removeEventListener('pointerdown', marcar, true);
      document.removeEventListener('keydown', marcar, true);
      document.removeEventListener('focusin', aoFocar);
    };
  }, []);

  const acao = useMutation({
    mutationFn: async (qual: 'pausar' | 'retomar' | 'ativar' | 'faturar') => ({ qual, r: await post<{ emitidas?: number; falhas?: unknown[] }>(`${CAMINHO_CONTRATOS}/${c.id}/${qual}`) }),
    onSuccess: async ({ qual, r }) => {
      await invalidarAssinaturas(qc);
      if (qual === 'faturar') {
        const falhas = r.falhas?.length ?? 0;
        if (falhas) toast.erro(t('mensagens.comFalha', { count: falhas }));
        else toast.sucesso(r.emitidas ? t('mensagens.faturado', { count: r.emitidas }) : t('mensagens.nadaAFaturar'));
      } else toast.sucesso(t(`mensagens.${qual === 'pausar' ? 'pausado' : qual === 'retomar' ? 'retomado' : 'ativado'}`));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const encerrar = useMutation({
    mutationFn: () => post<{ canceladas: number; faturas_canceladas: number; emitidas_futuras: unknown[] }>(`${CAMINHO_CONTRATOS}/${c.id}/encerrar`, { confirmar: true, motivo: motivo.trim() || null }),
    onSuccess: async (r) => {
      setEncerrando(false);
      await invalidarAssinaturas(qc);
      toast.sucesso(t('mensagens.encerrado'));
      if (r.faturas_canceladas) toast.info(t('encerrar.faturas', { count: r.faturas_canceladas }));
      if (r.emitidas_futuras.length) toast.info(t('encerrar.emitidas', { count: r.emitidas_futuras.length }));
    },
    onError: (e) => (setEncerrando(false), toast.erro(mensagemDeErro(e))),
  });
  // Pacote 2 (onda 1a): reativar o contrato encerrado (volta a faturar a partir de hoje).
  const reativar = useMutation({
    mutationFn: () => post<{ emitidas: number; falhas: unknown[] }>(`${CAMINHO_CONTRATOS}/${c.id}/reativar`, { confirmar: true }),
    onSuccess: async (r) => {
      setReativando(false);
      await invalidarAssinaturas(qc);
      toast.sucesso(t('mensagens.reativado'));
      if (r.falhas.length) toast.erro(t('mensagens.comFalha', { count: r.falhas.length }));
    },
    onError: (e) => (setReativando(false), toast.erro(mensagemDeErro(e))),
  });
  const excluir = useMutation({
    mutationFn: () => del(`${CAMINHO_CONTRATOS}/${c.id}`),
    onSuccess: async () => {
      setExcluindo(false);
      await invalidarAssinaturas(qc);
      toast.sucesso(tn('comum.removido'));
      aoExcluir();
    },
    onError: (e) => (setExcluindo(false), toast.erro(mensagemDeErro(e))),
  });

  // Lote de 19/10 (item 9): o campo escondido em Configurações > Campos das telas some do detalhe (o dado continua).
  const tela = useCamposDaTela('assinaturas.contrato');
  // [rótulo, valor, é número (fonte mono)]
  const resumo: [string, string, boolean][] = [
    [t('campos.valorCiclo'), dinheiro(c.valor_ciclo_centavos, c.moeda), true],
    [t('campos.mrr'), c.tipo === 'recorrente' && (c.estado === 'ativo' || c.estado === 'pausado') ? dinheiro(c.mrr_centavos, c.moeda) : '', true],
    [t('campos.intervalo'), textoRecorrencia(t, c), false],
    ...(c.plano_id && tela.visivel('plano_id') ? ([[t('campos.plano'), c.plano_nome ?? t('campos.planoInativo'), false]] as [string, string, boolean][]) : []),
    [t('campos.inicio'), data(c.inicio), true],
    [t('campos.termino'), c.termino ? data(c.termino) : t('fins.indeterminado'), !!c.termino],
    [t('campos.indice'), t(`indices.${c.indice}`), false],
    [t('campos.fator'), numero(Number(c.fator), 4), true],
    [t('campos.momento'), t(`momentos.${c.momento_pagamento}`), false],
  ];

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <Selo cor={COR_ESTADO[c.estado]}>{t(`estados.${c.estado}`)}</Selo>
        {c.inadimplente_desde && (c.estado === 'ativo' || c.estado === 'pausado') && <Selo cor="perigo">{t('inadimplencia.selo')}</Selo>}
        <span className="min-w-0 text-sm break-words text-tinta-2">{nomeParceiro(c.parceiro_nome)}</span>
        {c.referencia_externa && tela.visivel('referencia_externa') && <span className="font-mono text-xs text-tinta-3">{c.referencia_externa}</span>}
      </div>

      <div ref={topo} tabIndex={-1} className="flex flex-wrap gap-2 focus:outline-none">
        {podeEditar && (
          <Botao icone={<Pencil className="size-4" />} onClick={aoEditar}>
            {t('acoes.editar')}
          </Botao>
        )}
        {podePausar && c.estado === 'ativo' && (
          <Botao icone={<CirclePause className="size-4" />} carregando={acao.isPending && acao.variables === 'pausar'} onClick={() => acao.mutate('pausar')}>
            {t('acoes.pausar')}
          </Botao>
        )}
        {podePausar && podeEmitir && c.estado === 'pausado' && (
          <Botao icone={<CirclePlay className="size-4" />} carregando={acao.isPending && acao.variables === 'retomar'} onClick={() => acao.mutate('retomar')}>
            {t('acoes.retomar')}
          </Botao>
        )}
        {podePausar && podeEmitir && c.estado === 'rascunho' && (
          <Botao variante="primario" icone={<CirclePlay className="size-4" />} carregando={acao.isPending && acao.variables === 'ativar'} onClick={() => acao.mutate('ativar')}>
            {t('acoes.ativar')}
          </Botao>
        )}
        {podeFaturar && podeEmitir && c.estado === 'ativo' && c.falhas > 0 && (
          <Botao icone={<RotateCcw className="size-4" />} carregando={acao.isPending && acao.variables === 'faturar'} onClick={() => acao.mutate('faturar')}>
            {t('acoes.faturar')}
          </Botao>
        )}
        {s.instalado('documentos') && s.pode('documentos.criar') && c.estado !== 'encerrado' && (
          <Botao icone={<FileSignature className="size-4" />} onClick={() => setGerandoDocumento(true)}>
            {t('acoes.gerarDocumento')}
          </Botao>
        )}
        {s.instalado('documentos') && s.pode('documentos.criar') && c.estado !== 'encerrado' && (
          <Botao icone={<FilePen className="size-4" />} onClick={() => setGerandoAditivo(true)}>
            {t('acoes.gerarAditivo')}
          </Botao>
        )}
        {s.instalado('documentos') && s.pode('documentos.criar') && (
          <Botao icone={<FilePlus2 className="size-4" />} onClick={() => setAnexandoAssinado(true)}>
            {t('acoes.anexarAssinado')}
          </Botao>
        )}
        {s.pode('assinaturas.encerrar') && (c.estado === 'ativo' || c.estado === 'pausado') && (
          <Botao variante="perigo" icone={<CircleStop className="size-4" />} onClick={() => setEncerrando(true)}>
            {t('acoes.encerrar')}
          </Botao>
        )}
        {s.pode('assinaturas.encerrar') && podeEmitir && c.estado === 'encerrado' && (
          <Botao icone={<CirclePlay className="size-4" />} onClick={() => setReativando(true)}>
            {t('acoes.reativar')}
          </Botao>
        )}
        {s.pode('assinaturas.excluir') && c.estado === 'rascunho' && (
          <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
            {t('acoes.excluir')}
          </Botao>
        )}
      </div>

      {gerandoDocumento && <GerarDocumento contratoId={c.id} aoFechar={() => setGerandoDocumento(false)} />}
      {gerandoAditivo && <GerarDocumento aditivo contratoId={c.id} estado={c.estado} inadimplente={!!c.inadimplente_desde} aoFechar={() => setGerandoAditivo(false)} />}
      {anexandoAssinado && <AnexarAssinado contratoId={c.id} aoFechar={() => setAnexandoAssinado(false)} />}

      <section aria-label={t('detalhe.resumo')}>
        <dl className="grid min-w-0 grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          {resumo.map(([rotulo, valor, ehNumero]) => (
            <div key={rotulo} className="min-w-0">
              <dt className="text-xs text-tinta-3">{rotulo}</dt>
              <dd className={`${ehNumero ? 'numero ' : ''}font-medium break-words text-tinta-1`}>{anunciarOcultos(valor)}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* 22/10 (contrato por vertical): os serviços com a recorrência de cada um (a do contrato quando não têm a própria). */}
      {c.itens.length > 0 && (
        <section aria-label={t('itens.titulo')} className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold text-tinta-1">{t('itens.titulo')}</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {c.itens.map((i, n) => (
              <li key={n} className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-borda px-3 py-2">
                <span className="min-w-0 flex-1 break-words font-medium text-tinta-1">{textoLivre(i.descricao, c.parceiro_nome)}</span>
                <span className="numero text-tinta-2">
                  {numero(i.quantidade)} × {anunciarOcultos(dinheiro(i.preco_centavos, c.moeda))}
                </span>
                <span className="numero font-medium text-tinta-1">{anunciarOcultos(dinheiro(totalDaLinha(i.quantidade, i.preco_centavos), c.moeda))}</span>
                <Selo cor={i.recorrencia === 'avulso' ? 'azul' : 'neutro'}>{i.recorrencia ? t(`recorrenciaItem.${i.recorrencia}`) : textoRecorrencia(t, c)}</Selo>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label={t('campos.saude')} className={`rounded-lg border p-3 text-sm ${c.falhas || (c.inadimplente_desde && c.estado !== 'encerrado') ? 'border-perigo' : 'border-borda'}`}>
        <p className="font-semibold text-tinta-1">{t('campos.saude')}</p>
        <p className="mt-1 text-tinta-2">{c.ultima_competencia ? t('saude.ultima', { competencia: mesAno(c.ultima_competencia) }) : t('saude.semEmissao')}</p>
        <p className="text-tinta-2">{c.proxima_competencia && c.proxima_geracao ? t('saude.proxima', { competencia: mesAno(c.proxima_competencia), data: data(c.proxima_geracao) }) : t('saude.semProxima')}</p>
        <p className={c.falhas ? 'font-medium text-perigo' : 'text-verde'}>{c.falhas ? t('saude.falhas', { count: c.falhas }) : t('saude.emDia')}</p>
        {c.inadimplente_desde && (c.estado === 'ativo' || c.estado === 'pausado') && <p className="font-medium text-perigo">{t('inadimplencia.desde', { data: data(c.inadimplente_desde) })}</p>}
        {c.reativado_em && c.estado !== 'encerrado' && <p className="text-tinta-2">{t('inadimplencia.reativadoEm', { data: data(c.reativado_em) })}</p>}
        {c.falhas > 0 && c.ultimo_erro && <p className="mt-1 break-words text-tinta-2">{t('saude.ultimoErro', { erro: traduzirErro(c.ultimo_erro_codigo ?? undefined, c.ultimo_erro) })}</p>}
        {s.pode('contas.ver') && c.ultima_competencia && (
          <Link className="mt-2 inline-flex items-center gap-1 text-acento-forte underline" to={`/financeiro/contas/a-receber?parceiro_id=${c.parceiro_id}&estado=todos`}>
            <ExternalLink className="size-4" aria-hidden />
            {t('acoes.verFatura')}
          </Link>
        )}
      </section>

      <section aria-label={t('detalhe.cobrancas')} className="min-w-0">
        <h3 className="text-sm font-semibold text-tinta-1">{t('detalhe.cobrancas')}</h3>
        <p className="mb-2 text-xs text-tinta-3">{t(c.unidade_recorrencia === 'dia' || c.unidade_recorrencia === 'semana' ? 'detalhe.cobrancasLegendaDias' : 'detalhe.cobrancasLegenda')}</p>
        {cursorAnteriores && (
          <div className="mb-2 flex justify-center">
            <Botao variante="fantasma" tamanho="sm" carregando={carregandoAnteriores} onClick={(e) => carregarMais(buscarAnteriores, e.currentTarget, { anteriores: true })}>
              {t('detalhe.carregarAnteriores')}
            </Botao>
          </div>
        )}
        {!cobrancas.length ? (
          <p className="text-sm text-tinta-3">{t('detalhe.semCobrancas')}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {cobrancas.map((x) => (
              <li key={x.id} className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-borda px-3 py-2">
                <span className={`numero font-medium text-tinta-1 ${x.periodo ? 'min-w-20' : 'w-20'}`}>{x.periodo ? data(x.periodo) : mesAno(x.competencia)}</span>
                <span className="text-tinta-3">
                  {t('campos.geracao')}: <span className="numero text-tinta-2">{data(x.data_geracao)}</span>
                </span>
                <span className="text-tinta-3">
                  {t('campos.vencimento')}: <span className="numero text-tinta-2">{data(x.vencimento)}</span>
                </span>
                <span className="numero font-medium text-tinta-1">{anunciarOcultos(dinheiro(x.valor_centavos, x.moeda))}</span>
                <Selo cor={COR_COBRANCA[x.estado]}>{t(`estadosCobranca.${x.estado}`)}</Selo>
                {x.reajuste_id && <Selo cor="roxo">{t('detalhe.reajustada')}</Selo>}
                {x.motivo_cancelamento && <span className="text-xs text-tinta-3">{t(`motivos.${x.motivo_cancelamento}`)}</span>}
                {x.estado === 'falhou' && x.erro && <span className="w-full text-xs break-words text-perigo">{traduzirErro(x.erro_codigo ?? undefined, x.erro)}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label={t('detalhe.reajustes')} className="min-w-0">
        <h3 className="mb-2 text-sm font-semibold text-tinta-1">{t('detalhe.reajustes')}</h3>
        {!c.reajustes.length ? (
          <p className="text-sm text-tinta-3">{t('detalhe.semReajustes')}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {c.reajustes.map((r) => (
              <li key={r.id} className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-borda px-3 py-2">
                <span className="text-tinta-2">{t('reajustes.aniversario', { mes: mesAno(r.aniversario.slice(0, 7)) })}</span>
                <span className="text-tinta-2">
                  {t(`indices.${r.indice}`)} {t('reajustes.acumulado', { pct: `${numero(r.acumulado_pct, 2)}%` })}
                </span>
                <span className="numero text-tinta-3 line-through">{anunciarOcultos(dinheiro(r.valor_antigo_centavos, c.moeda))}</span>
                <span className="numero font-medium text-tinta-1">{anunciarOcultos(dinheiro(r.valor_novo_centavos, c.moeda))}</span>
                <Selo cor={r.estado === 'aprovado' ? 'verde' : r.estado === 'recusado' ? 'neutro' : 'laranja'}>{t(`reajustes.estado.${r.estado}`)}</Selo>
                {r.decidido_em && <span className="numero text-xs text-tinta-3">{dataHora(r.decidido_em)}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Trilha 2 (Pix Sicredi): banco que gera a cobrança das faturas deste contrato (vence o do cliente e o da empresa). */}
      {s.instalado('contas') && s.pode('contas.ver') && <BancoDaCobranca contratoId={c.id} />}

      {c.observacoes_fatura && tela.visivel('observacoes_fatura') && <p className="text-sm break-words text-tinta-2">{textoLivre(c.observacoes_fatura, c.parceiro_nome)}</p>}

      <div className="border-t border-borda pt-4">
        <Chatter entidade="assinaturas.contrato" registroId={c.id} nomes={[c.parceiro_nome]} />
      </div>

      <Confirmar
        aberto={encerrando}
        aoFechar={() => setEncerrando(false)}
        aoConfirmar={() => encerrar.mutate()}
        carregando={encerrar.isPending}
        perigoso
        titulo={t('encerrar.titulo')}
        texto={
          <div className="flex flex-col gap-3">
            <p>{t('encerrar.texto', { count: pendentes })}</p>
            <Rotulo id="motivo-encerramento" rotulo={t('encerrar.motivo')}>
              <AreaSensivel id="motivo-encerramento" maxLength={500} rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
            </Rotulo>
          </div>
        }
      />
      <Confirmar aberto={reativando} aoFechar={() => setReativando(false)} aoConfirmar={() => reativar.mutate()} carregando={reativar.isPending} titulo={t('reativar.titulo')} texto={t('reativar.texto')} />
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => excluir.mutate()} carregando={excluir.isPending} perigoso titulo={t('excluir.titulo')} texto={t('excluir.texto')} />
    </div>
  );
}

const PROVEDORES = ['asaas', 'sicredi', 'nenhum'] as const;
type Provedor = (typeof PROVEDORES)[number];

/** Trilha 2 (Pix Sicredi): regra do contrato (`/contas/cobranca/provedor/contrato/:id`); grava só no Salvar. */
function BancoDaCobranca({ contratoId }: { contratoId: string }) {
  const { t } = useTranslation('assinaturas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const chave = `/contas/cobranca/provedor/contrato/${contratoId}`;
  const q = useQuery({ queryKey: [chave], queryFn: () => get<{ provedor: Provedor | null }>(chave) });
  const [valor, setValor] = useState<Provedor | ''>('');
  useEffect(() => {
    if (q.data) setValor(q.data.provedor ?? '');
  }, [q.data]);
  const salvar = useMutation({
    mutationFn: () => api('PUT', chave, { provedor: valor || null }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [chave] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!q.data) return null;
  const podeEditar = s.pode('contas.configurar');
  const id = `banco-cobranca-${contratoId}`;
  return (
    <section aria-label={t('banco.titulo')} className="min-w-0" data-banco-cobranca>
      <h3 className="mb-2 text-sm font-semibold text-tinta-1">{t('banco.titulo')}</h3>
      <div className="flex min-w-0 flex-wrap items-end gap-3">
        <Rotulo id={id} rotulo={t('banco.rotulo')} ajuda={t('banco.ajuda')}>
          <Selecao id={id} aria-describedby={`${id}-ajuda`} disabled={!podeEditar} value={valor} onChange={(e) => setValor(e.target.value as Provedor | '')} vazio={t('banco.segue')} opcoes={PROVEDORES.map((p) => ({ valor: p, rotulo: t(`banco.opcoes.${p}`) }))} />
        </Rotulo>
        {podeEditar && (
          <Botao variante="secundario" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {tn('comum.salvar')}
          </Botao>
        )}
      </div>
    </section>
  );
}
